import { Injectable } from '@angular/core';
import { Answer, Feedback, TypeAttachment } from '@app/core/models';
import { BehaviorSubject, Observable } from 'rxjs';

interface AnswerData extends Answer {
  is_selected: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ModalReviewService {
  private _studentId$ = new BehaviorSubject<number>(undefined);
  private _answers$ = new BehaviorSubject<AnswerData[]>([]);

  public get studentId$(): Observable<number> {
    return this._studentId$.asObservable();
  }

  public get answers$(): Observable<AnswerData[]> {
    return this._answers$.asObservable();
  }

  public setStudentId(studentId: number): void {
    this._studentId$.next(studentId);
  }

  public updatedAnswers(answers: Answer[] = [], student): void {
    answers = this.formatAnswers(answers);
    this._answers$.next(answers.map((answer) => ({ ...answer, is_selected: answer.student_id === student.id })));
  }

  public setAnswers(answers: Answer[] = []): void {
    answers = this.formatAnswers(answers);
    this._answers$.next(answers.map((answer) => ({ ...answer, is_selected: false })));
  }

  public editAnswer(answer: Answer): void {
    this._answers$.next(this._answers$.value.map((a) => (a.id === answer.id ? { ...a, ...answer } : a)));
  }

  public selectAnswer(answerId: string): void {
    this._answers$.next(this._answers$.value.map((answer) => ({ ...answer, is_selected: answer.id === answerId })));
  }

  public editFeedback(feedback: Feedback): void {
    this._answers$.next(
      this._answers$.value.map((answer) =>
        answer.id === feedback.answer_id
          ? {
              ...answer,
              feedback: answer?.feedback?.map((f) => (f.id === feedback.id ? feedback : f)),
            }
          : answer,
      ),
    );
  }

  public removeFeedback(feedback: Feedback): void {
    this._answers$.next(
      this._answers$.value.map((answer) =>
        answer.id === feedback.answer_id
          ? { ...answer, feedback: answer.feedback.filter((f) => f.id !== feedback.id) }
          : answer,
      ),
    );
  }

  public addFeedback(feedback: Feedback): void {
    this._answers$.next(
      this._answers$.value.map((answer) =>
        answer.id === feedback.answer_id ? { ...answer, feedback: [feedback, ...(answer?.feedback || [])] } : answer,
      ),
    );
  }

  public markAttachmentAsViewed(answerId: string, attachmentId: number, viewedAt: string) {
    this._answers$.next(
      this._answers$.value.map((answer) =>
        answer.id === answerId
          ? {
              ...answer,
              attachments: answer?.attachments?.map((a) => (a.id === attachmentId ? { ...a, viewed_at: viewedAt } : a)),
            }
          : answer,
      ),
    );
  }

  public formatAnswerItem(answer: Answer): Answer {
    let attachments = answer.attachments;
    if (!!answer.answer) {
      attachments = [
        {
          id: undefined,
          file: 'arquivo-html',
          title: 'Resposta',
          size: 0,
          extension: 'html',
          type: TypeAttachment.HTML,
          html: answer.answer,
          created_at: answer.created_at,
          viewed_at: answer.viewed_at,
        },
        ...answer.attachments ?? [],
      ];

    }
    return { ...answer, attachments };
  }

  private formatAnswers = (answers: Answer[] = []): Answer[] =>
    answers.filter((answer) => !!answer).map((answer) => this.formatAnswerItem(answer));
}
